const bimg = (id, crop = "entropy", w = 1400) =>
  `https://images.unsplash.com/${id}?q=80&w=${w}&auto=format&fit=crop&crop=${crop}`;

const P1 = bimg("photo-1519710164239-da123dc03ef4", "entropy");
const P2 = bimg("photo-1522771739844-6a9f6d5f14af");
const T1 = bimg("photo-1519823551278-64ac92734fb1", "faces");
const I1 = bimg("photo-1600210492486-724fe5c67fb0", "bottom");

export const PRENATAL_POSTS = [
  {
    slug: "is-prenatal-massage-safe",
    title: "Prenatal Massage: Is Massage Safe During Pregnancy?",
    keyword: "is prenatal massage safe",
    category: "Prenatal & Postnatal",
    read: "4 min read",
    img: P1,
    excerpt:
      "The short answer: yes, with the right training, positioning and timing. What makes prenatal massage safe and when to check with your provider first.",
    body: [
      {
        p: [
          "Prenatal massage, performed by a therapist trained in pregnancy work, is widely considered safe and beneficial for most pregnancies — easing back pain, hip discomfort, swelling and the simple weight of carrying a growing person. The safety is in the details: positioning, pressure choices and timing.",
        ],
      },
      {
        h: "What makes it safe",
        p: [
          "Side-lying or semi-reclined positioning (never flat on the stomach, and limited time flat on the back), pressure adapted to tissue that is softer and more vascular in pregnancy, no deep work on the legs where clot risk concentrates, and a therapist who knows the pregnancy-specific cautions by heart.",
        ],
      },
      {
        h: "When to ask your provider first",
        p: [
          "High-risk pregnancy, preeclampsia, significant swelling that appears suddenly, placenta complications, or any bleeding — these change the answer, and your midwife or physician should clear massage first. When in doubt, one phone call to your provider settles it.",
        ],
      },
    ],
    note:
      "Always tell your therapist how far along you are and share any complications your provider has flagged — prenatal sessions are tailored to your trimester and your pregnancy.",
  },
  {
    slug: "when-can-you-get-prenatal-massage",
    title: "When Can You Start Prenatal Massage?",
    keyword: "when can you get prenatal massage",
    category: "Prenatal & Postnatal",
    read: "3 min read",
    img: P2,
    excerpt:
      "Most clinics begin prenatal massage after the first trimester — why that convention exists and what each stage of pregnancy asks for.",
    body: [
      {
        p: [
          "The common convention — followed by most clinics and insurers — is to begin prenatal massage from the second trimester, after week twelve. This is not because massage is dangerous earlier; it is because the first trimester carries the highest natural risk of miscarriage, and everyone prefers that no one ever wonders whether an external factor played a role.",
        ],
      },
      {
        h: "Each stage, in brief",
        p: [
          "First trimester: gentle relaxation only, and ideally after your provider's blessing. Second trimester: the sweet spot — energy returns, the bump is manageable, and massage helps most. Third trimester: positioning adapts further; sessions focus on the lower back, hips, swollen legs and rest.",
        ],
      },
      {
        h: "The booking note",
        p: [
          "Mention your week of pregnancy when booking. The table setup, bolstering and session plan are prepared accordingly — you simply arrive and are looked after.",
        ],
      },
    ],
    note: null,
  },
  {
    slug: "prenatal-massage-first-trimester",
    title: "Massage in the First Trimester: What to Know",
    keyword: "prenatal massage first trimester",
    category: "Prenatal & Postnatal",
    read: "3 min read",
    img: P1,
    excerpt:
      "Exhausted, nauseous, and wondering if massage is allowed? The careful first-trimester picture, without alarmism and without carelessness.",
    body: [
      {
        p: [
          "The first trimester is when you may want massage most — exhaustion, nausea, the adrenaline of a life-changing secret — and when clinics are most conservative about providing it.",
        ],
      },
      {
        h: "Why clinics are cautious",
        p: [
          "There is no good evidence that gentle massage harms a first-trimester pregnancy. The caution is about association: early miscarriage is sadly common and happens regardless of massage, and no therapist wants to be anywhere near that coincidence. Hence the widespread “from twelve weeks” policy.",
        ],
      },
      {
        h: "If you want bodywork now",
        p: [
          "Ask your provider first. With their blessing, a first-trimester session is a gentle, relaxation-focused appointment — no deep abdominal or lower-back work, moderate pressure, comfortable positioning. Sometimes the most therapeutic option these weeks is simply rest, and an honest therapist will say so.",
        ],
      },
    ],
    note:
      "Any bleeding, significant pain, or a high-risk diagnosis means massage waits for your provider's explicit clearance.",
  },
  {
    slug: "prenatal-massage-second-third-trimester",
    title: "Massage in the Second and Third Trimester",
    keyword: "prenatal massage second third trimester",
    category: "Prenatal & Postnatal",
    read: "4 min read",
    img: P2,
    excerpt:
      "The months when prenatal massage shines most — and how sessions adapt as the bump grows.",
    body: [
      {
        p: [
          "From week thirteen onward, prenatal massage comes into its own. The body is now visibly, physically changing every week — and the complaints are muscular at heart: lower back, hips, legs, shoulders, sleep.",
        ],
      },
      {
        h: "Second trimester",
        p: [
          "Energy typically returns and the bump is still modest. Sessions address the postural shift as the centre of gravity moves: the lower back and hips, the mid-back that compensates above, and the legs that carry the extra load. Positioning is side-lying or reclined with bolsters.",
        ],
      },
      {
        h: "Third trimester",
        p: [
          "Sessions become shorter on any single position and longer on care: swelling in the legs and feet, the aching pelvis, the ribs the baby now visits, and rest — deep, rare, precious rest. Many clients say the third-trimester sessions are what kept them comfortable through the final stretch.",
        ],
      },
    ],
    note:
      "Sudden severe swelling, headaches with visual changes, or reduced fetal movement need immediate contact with your provider — not a massage booking.",
  },
  {
    slug: "prenatal-massage-for-back-pain",
    title: "Prenatal Massage for Back Pain in Pregnancy",
    keyword: "prenatal massage for back pain",
    category: "Prenatal & Postnatal",
    read: "4 min read",
    img: T1,
    excerpt:
      "Up to half of pregnancies come with back pain — and painkillers are mostly off the table. How prenatal massage fills that gap safely.",
    body: [
      {
        p: [
          "Pregnancy back pain has perfect conditions: a shifting centre of gravity, the hormone relaxin softening ligaments, and a spine adapting weekly. Meanwhile, most pain medication is off-limits. That combination makes prenatal massage less of a luxury and more of a front-line comfort measure.",
        ],
      },
      {
        h: "What a session targets",
        p: [
          "The lower back muscles working overtime, the glutes and piriformis (pregnancy sciatica often lives here), the mid-back compensating from above, and the hips that soften and widen. All in side-lying or reclined positioning with pillows, at pregnancy-adapted pressure.",
        ],
      },
      {
        h: "Between sessions",
        p: [
          "A pillow between the knees at night, breaking up long sitting, and gentle cat-cow movements protect the progress. Small, frequent care suits a body changing this fast.",
        ],
      },
    ],
    note:
      "Back pain with regular contractions, bleeding or fluid leaking can signal preterm labour — contact your provider immediately.",
  },
  {
    slug: "prenatal-massage-for-swollen-legs",
    title: "Prenatal Massage for Swollen Legs and Feet",
    keyword: "prenatal massage for swollen legs",
    category: "Prenatal & Postnatal",
    read: "3 min read",
    img: P1,
    excerpt:
      "The ankle bones disappear by the third trimester — normal, uncomfortable, and very responsive to the right kind of massage.",
    body: [
      {
        p: [
          "Pregnancy increases blood volume by nearly half, and the growing uterus slows the return flow from the legs. The result is the familiar third-trimester picture: puffy feet, vanished ankles, calves that feel full by evening.",
        ],
      },
      {
        h: "How massage helps",
        p: [
          "Gentle, rhythmic strokes directed toward the heart encourage the excess fluid along — many clients see their ankle bones again by the end of a session. The work is deliberately light: pregnancy changes clotting, so deep leg pressure is avoided in favour of slow, superficial lymphatic-style strokes that are both safer and more effective for swelling.",
        ],
      },
      {
        h: "At home",
        p: [
          "Feet up above heart level when you can, sleeping on the left side, and water — counterintuitively, good hydration reduces water retention.",
        ],
      },
    ],
    note:
      "Sudden or severe swelling, swelling in the face or hands, or one leg swelling much more than the other needs same-day contact with your provider — these can signal preeclampsia or a clot.",
  },
  {
    slug: "prenatal-massage-positioning",
    title: "How Do You Lie During a Prenatal Massage?",
    keyword: "prenatal massage positioning",
    category: "Prenatal & Postnatal",
    read: "3 min read",
    img: P2,
    excerpt:
      "No face-down, minimal flat-on-back — yet deeply comfortable. How bolstering and side-lying make prenatal massage work.",
    body: [
      {
        p: [
          "The most common prenatal question is logistical: where does the bump go? The answer is a carefully engineered nest of pillows.",
        ],
      },
      {
        h: "Side-lying: the main position",
        p: [
          "Most of a prenatal session happens with you lying on your side — usually the left, which best supports circulation — supported by pillows under the head, between the knees, and hugged against the bump. Properly bolstered, side-lying is not a compromise; clients regularly fall asleep in it.",
        ],
      },
      {
        h: "Semi-reclined: for back and shoulders",
        p: [
          "For neck, shoulder, face and foot work you rest semi-reclined — on your back at a supported angle, never flat for long. Face-down positioning is not used after the first trimester; any table with a “pregnancy hole” is a relic to avoid, as it suspends the abdomen by the ligaments.",
        ],
      },
    ],
    note: null,
  },
  {
    slug: "prenatal-massage-therapist-near-me",
    title: "How to Choose a Prenatal Massage Therapist",
    keyword: "prenatal massage therapist near me",
    category: "Prenatal & Postnatal",
    read: "3 min read",
    img: I1,
    excerpt:
      "Pregnancy raises the bar for who touches you. The specific questions that separate a trained prenatal therapist from a generalist.",
    body: [
      {
        p: [
          "Every registered therapist can offer relaxation; pregnancy asks for more specific knowledge. A few questions quickly reveal whether a therapist is genuinely prepared.",
        ],
      },
      {
        h: "What to ask before booking",
        p: [
          "Have you trained specifically in prenatal massage? How will I be positioned at my week of pregnancy? What will you avoid or adapt? Confident, specific answers — side-lying bolstering, leg-pressure cautions, trimester-by-trimester adaptation — signal real training. Vague reassurance does not.",
        ],
      },
      {
        h: "The green flags",
        p: [
          "A health intake that asks about your pregnancy in detail, a therapist who asks about your provider's guidance on anything borderline, and a room where you feel safe enough to actually rest. In Edmonton, Royal Essence offers prenatal sessions with exactly this preparation — your trimester shapes the session.",
        ],
      },
    ],
    note: null,
  },
  {
    slug: "when-can-i-get-a-massage-postpartum",
    title: "When Can You Get a Massage After Giving Birth?",
    keyword: "when can I get a massage postpartum",
    category: "Prenatal & Postnatal",
    read: "3 min read",
    img: P1,
    excerpt:
      "Your body just did the hardest physical work of your life — and then got no recovery period. When postpartum massage can begin.",
    body: [
      {
        p: [
          "After an uncomplicated vaginal birth, gentle massage can typically begin within the first couple of weeks — whenever you feel ready to be touched and can arrange an hour away. After a caesarean birth, wait for your provider's clearance, usually around six weeks, with scar-area work later still.",
        ],
      },
      {
        h: "What early sessions focus on",
        p: [
          "Not “fixing” anything — restoration. The shoulders and neck of feeding and rocking, the mid-back of carrying, the hips and pelvis of the birth itself, and plain exhaustion. Pressure is gentle; the goal is to be cared for, finally, after months of giving care from your own body.",
        ],
      },
      {
        h: "Practical kindness",
        p: [
          "Sessions can pause for feeding, positions adapt to tender areas, and arriving in whatever state the morning allowed is completely fine. Postpartum massage meets you exactly where you are — including with the baby in the room if that is what makes an hour possible.",
        ],
      },
    ],
    note:
      "Fever, worsening pain, heavy bleeding or signs of infection after birth need your provider, not a massage table.",
  },
  {
    slug: "postnatal-massage-edmonton",
    title: "Postnatal Massage in Edmonton: Support for Recovery After Birth",
    keyword: "postnatal massage Edmonton",
    category: "Prenatal & Postnatal",
    read: "4 min read",
    img: P2,
    excerpt:
      "The fourth trimester is the hardest and the least supported. What postnatal massage addresses — body, sleep debt, and the hour that is finally yours.",
    body: [
      {
        p: [
          "Postpartum care in our culture stops at six weeks and one checkup, while the body's real recovery takes a year. Postnatal massage exists in that gap: structured care for the person everyone stopped asking about.",
        ],
      },
      {
        h: "What sessions address",
        p: [
          "The physical: neck and shoulders reshaped by feeding positions, the forearms of holding, the lower back and pelvis settling after pregnancy, and the core region gently reawakened. The systemic: broken-sleep fatigue and a nervous system running on alert. Sessions restore tissue and — not incidentally — give you an hour where you are the one being looked after.",
        ],
      },
      {
        h: "At Royal Essence",
        p: [
          "Postnatal sessions in Edmonton are shaped entirely around your recovery stage and your day's reality — pressure, positioning, even the length of quiet. Feeding breaks are normal, timing is flexible, and nothing about your body needs an apology. You did something enormous; recovery deserves structure too.",
        ],
      },
    ],
    note: null,
  },
];
